import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { sendEmail, EMAILJS_CONFIG, isEmailJSConfigured } from '../../../utils/emailjs';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (!isEmailJSConfigured()) {
        console.warn('EmailJS is not configured. Please update src/utils/emailjs.ts with your credentials.');
        setSubmitStatus('error');
        return;
      }

      await sendEmail(EMAILJS_CONFIG.CONTACT_TEMPLATE_ID, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden mt-24 rounded-t-[3rem] mx-4">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://static.readdy.ai/image/4c54787fd2047152d108467e5113439e/e5fe30b4f26c08cfb369c5b19d957793.png"
                alt="Sri Maruthi Teeth Empire Logo"
                className="w-12 h-12 object-contain rounded-lg"
              />
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sri Maruthi Teeth Empire
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-6">Your Partner in Dental Health</p>
            <div className="mb-6">
              <h4 className="text-xl font-bold mb-3">Ready to Transform Your Smile?</h4>
              <button
                onClick={() => {
                  const element = document.getElementById('booking');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </button>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a href="#hero" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Home
                </a>
              </li>
              <li>
                <a href="#overview" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  About Us
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Services
                </a>
              </li>
              <li>
                <a href="#testimonials" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Testimonials
                </a>
              </li>
              <li>
                <a href="#booking" className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                  Book Appointment
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Get in Touch</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-phone-line text-teal-400 text-lg mt-1"></i>
                <div>
                  <a href="tel:+918340978930" className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                    +918340978930
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-teal-400 text-lg mt-1"></i>
                <div>
                  <a href="mailto:teethempire08@gmail.com" className="text-gray-300 hover:text-teal-400 transition-colors text-sm cursor-pointer">
                    teethempire08@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line text-teal-400 text-lg mt-1"></i>
                <div className="text-gray-300 text-sm">
                  Ragini Residency, second floor 2B, Beside Dominos, Near Chirec International School, Botanical Garden Road, Kondapur, Hyderabad, Telangana, 5400084
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-whatsapp-line text-green-400 text-lg mt-1"></i>
                <div>
                  <a
                    href="https://wa.me/918340978930"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-gray-300 hover:text-green-400 transition-colors text-sm cursor-pointer"
                  >
                    +918340978930
                  </a>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Contact Us</h4>
            <p className="text-gray-400 text-sm mb-4">Have questions? Send us a message</p>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Your Name"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="Your Email"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                placeholder="Phone Number"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="Subject"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                maxLength={500}
                rows={3}
                placeholder="Your Message"
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
              ></textarea>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white text-slate-900 py-2.5 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <i className="ri-send-plane-fill"></i>
              </button>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">Failed to send. Please try again.</p>
              )}
            </form>

            <div className="flex gap-4 mt-6">
              <a
                href="https://www.instagram.com/sri_maruthi_teeth_empire?igsh=YWpsdWRtZWxmb3V2"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-teal-500 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 Sri Maruthi Teeth Empire Dental Clinic, Kondapur, Hyderabad. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer">
                Terms of Service
              </Link>
              <a
                href="https://www.growthlink.in"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer"
              >
                Powered by&nbsp; Growthlink
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 text-center opacity-5 pointer-events-none">
          <h2 className="text-9xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            SRI MARUTHI TEETH EMPIRE
          </h2>
        </div>
      </div>
    </footer>
  );
}
